<template>
  <div>
    <!-- Desktop view (md and up) -->
    <div class="d-none d-md-block">
      <UiCard :title="title" elevation="1">
        <ScrollArea max-height="400px">
          <!-- Course Items -->
          <div
            v-for="(course, index) in courses"
            :key="course.key || index"
            class="pt-2 pb-2"
            style="border-bottom: 1px solid var(--asu-grayscale-gray-4)"
          >
            <div class="mb-2">
              <UiChip size="small" variant="default">{{ course.coursePrefix }}</UiChip>
              <h5
                class="font-weight-regular ma-0 mt-1"
                style="line-height: 1.3; color: #333; font-size: 15px; font-weight: 600"
              >
                {{ course.courseName }}
              </h5>
            </div>

            <div class="d-flex align-center justify-space-between">
              <!-- Left side info -->
              <div>
                <span
                  v-if="course.startDate"
                  class="text-body-2"
                  style="font-size: 14px; font-weight: 500; color: gray"
                >
                  Starts {{ course.startDate }}
                </span>
                <div v-else-if="course.finalGrade" class="d-flex flex-column">
                  <span class="text-body-2" style="font-size: 14px; font-weight: 500; color: gray">FINAL GRADE</span>
                  <span style="font-size: 21px; font-weight: 800">{{ course.finalGrade }}</span>
                </div>
              </div>

              <!-- Right side info - Credits and Price -->
              <div class="d-flex align-center" style="gap: 16px">
                <span class="text-body-2" style="font-size: 14px; color: #333"> {{ course.credits }} Credits </span>
                <span class="text-body-2" style="font-size: 14px; color: #333"> ${{ course.price }} </span>
              </div>
            </div>

            <!-- Delete option (optional) -->
            <div v-if="showDelete" class="mt-2">
              <a
                href="#"
                class="text-decoration-none text-body-2"
                style="color: #d32f2f; transition: color 0.2s ease-in-out"
                @click.prevent="$emit('remove-course', course)"
                @mouseover="
                  $event.target.style.color = '#b71c1c';
                  $event.target.style.textDecoration = 'underline';
                "
                @mouseout="
                  $event.target.style.color = '#d32f2f';
                  $event.target.style.textDecoration = 'none';
                "
              >
                Delete
              </a>
            </div>

            <!-- Warning (optional) -->
            <div v-if="course.warning" class="mt-2 text-body-2" style="color: #747474">
              <v-icon size="15" color="#747474" class="me-1">mdi-alert</v-icon>
              <strong>Please note:</strong> {{ course.warning }}
            </div>
          </div>
        </ScrollArea>

        <!-- Total Section -->
        <div v-if="showTotal" class="py-2" :class="{ 'mt-2': courses.length > 0 }">
          <div class="d-flex justify-space-between align-center">
            <span class="text-body-2 font-weight-bold" style="color: #747474; font-size: 14px; letter-spacing: 0.5px">
              {{ totalLabel }}
            </span>
            <div class="d-flex align-center" style="gap: 16px">
              <span class="text-body-2 font-weight-bold" style="font-size: 16px; color: #191919">
                {{ totalCredits }} Credits
              </span>
              <div class="d-flex align-center" style="gap: 8px">
                <span class="text-body-2 font-weight-bold" style="font-size: 16px" :style="{ color: totalPriceColor }">
                  <slot name="total-price"> ${{ totalPrice }} </slot>
                </span>
                <slot name="total-icon" />
              </div>
            </div>
          </div>
        </div>
        <!-- Additional content slot (for voucher, buttons, etc.) -->
        <slot name="additional-content" />
      </UiCard>
    </div>

    <!-- Mobile view (sm and down) - Sheet -->
    <Teleport to="body">
      <div v-if="$vuetify.display.smAndDown">
        <UiSheet v-model="isExpanded" position="bottom">
          <!-- Custom header slot -->
          <template #header="{ isExpanded: sheetExpanded }">
            <div class="d-flex align-center justify-space-between w-100">
              <h3 class="sheet-title">
                {{ title }}<span v-if="courses.length > 0"> ({{ courses.length }})</span>
              </h3>
              <v-icon :class="{ 'chevron-rotated': sheetExpanded }">mdi-chevron-up</v-icon>
            </div>
          </template>

          <!-- Content slot -->
          <template #default>
            <!-- Empty state -->
            <div v-if="courses.length === 0">
              <slot name="additional-content" />
            </div>

            <!-- Selected courses list -->
            <div v-else class="pa-4">
              <!-- Course Items -->
              <div
                v-for="(course, index) in courses"
                :key="course.key || index"
                class="pb-2 mb-2"
                style="border-bottom: 1px solid var(--asu-grayscale-gray-4)"
              >
                <div class="mb-2">
                  <UiChip size="small" variant="default">{{ course.coursePrefix }}</UiChip>
                  <h5 class="font-weight-bold mt-2 mb-2" style="font-size: 18px">{{ course.courseName }}</h5>
                </div>

                <div class="d-flex align-center justify-space-between mb-2" style="font-size: 14px; color: #333">
                  <span v-if="course.startDate">Starts {{ course.startDate }}</span>
                  <div v-else-if="course.finalGrade" class="d-flex flex-column">
                    <span class="text-body-2" style="font-size: 14px; font-weight: 500; color: gray">FINAL GRADE</span>
                    <span style="font-size: 21px; font-weight: 800">{{ course.finalGrade }}</span>
                  </div>
                  <div class="d-flex align-center" style="gap: 8px">
                    <span>{{ course.credits }} Credits</span>
                    <span>${{ course.price }}</span>
                  </div>
                </div>

                <!-- Delete option (optional) -->
                <a
                  v-if="showDelete"
                  href="#"
                  class="text-decoration-none"
                  style="color: #d32f2f; font-size: 14px; font-weight: 500"
                  @click.prevent="$emit('remove-course', course)"
                >
                  Delete
                </a>

                <!-- Warning (optional) -->
                <div v-if="course.warning" class="mt-2 text-body-2" style="color: #747474">
                  <v-icon size="15" color="#747474" class="me-1">mdi-alert</v-icon>
                  <strong>Please note:</strong> {{ course.warning }}
                </div>
              </div>

              <!-- Total Section -->
              <div v-if="showTotal" class="d-flex justify-space-between align-center py-4">
                <span class="font-weight-bold" style="font-size: 14px; color: #666; letter-spacing: 0.5px">{{
                  totalLabel
                }}</span>
                <div class="d-flex align-center" style="gap: 16px">
                  <span class="font-weight-bold" style="font-size: 16px">{{ totalCredits }} Credits</span>
                  <span class="font-weight-bold" style="font-size: 16px" :style="{ color: totalPriceColor }">
                    <slot name="total-price"> ${{ totalPrice }} </slot>
                  </span>
                </div>
              </div>

              <!-- Additional content slot (for buttons, etc.) -->
              <slot name="additional-content" />
            </div>
          </template>
        </UiSheet>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { Card as UiCard, Chip as UiChip, ScrollArea } from '@/components/ui';
import UiSheet from './Sheet.vue';

export default defineComponent({
  name: 'SelectedCourseCard',

  components: {
    UiCard,
    UiChip,
    ScrollArea,
    UiSheet
  },

  data() {
    return {
      isExpanded: false
    };
  },

  props: {
    title: {
      type: String,
      default: 'Selected Courses'
    },
    courses: {
      type: Array,
      required: true,
      // Each course should have: { coursePrefix, courseName, credits, price, startDate OR finalGrade, key?, warning? }
      default: () => []
    },
    showDelete: {
      type: Boolean,
      default: false
    },
    showTotal: {
      type: Boolean,
      default: false
    },
    totalLabel: {
      type: String,
      default: 'ESTIMATED TOTAL'
    },
    totalPriceColor: {
      type: String,
      default: '#2ba206'
    }
  },

  emits: ['remove-course'],

  computed: {
    totalCredits() {
      return this.courses.reduce((sum, course) => sum + (parseInt(course.credits) || 0), 0);
    },
    totalPrice() {
      return this.courses.reduce((sum, course) => sum + (parseFloat(course.price) || 0), 0);
    }
  }
});
</script>

<style scoped>
/* Sheet styling */
.sheet-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: #191919;
}

.chevron-rotated {
  transform: rotate(180deg);
  transition: transform 0.3s ease;
}
</style>
